import { useEffect, useLayoutEffect, useSyncExternalStore } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter, useRouterState } from '@tanstack/react-router'
import { getProblemReportCapabilities, useGetProblemReportCapabilities } from '../../api/generated/problem-reports/problem-reports'
import { USE_REAL_API } from '../api'
import { fetchMe, shellForOrgType } from '../auth'
import { isStartCompanyBundle } from '../public-bundle'
import { useAuthStore } from '../../store/auth'
import { useOrgContextStore } from '../../store/orgContext'
import { useSupplyPersonaStore } from '../../store/role'
import { PROBLEM_REPORT_ACTORS, REPORT_SUPPLY_PERSONAS } from './report-actors'
import {
  assertReportRequest, clearReportIdentity, reportContextChanged, reportHostState, reportIdentityKey, reportQueryKey, setReportPolicy,
  subscribeReportHost, suspendReportIdentity, verifyReportIdentity,
} from './context'
import type { ReportIdentity } from './context'
import { problemReportEpoch, recordProblemReportAction, recordProblemReportError } from './runtime'
import { reportRouteName, reportShell } from './registry'

const reportMutationKeys = new Set(['createProblemReport', 'confirmProblemReport', 'reserveProblemReportAttachment', 'confirmProblemReportAttachment', 'commandAdminProblemReport'])

export function useProblemReportState() {
  return useSyncExternalStore(subscribeReportHost, reportHostState, reportHostState)
}

export function useProblemReportHost() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const pathname = useRouterState({ select: state => state.location.pathname })
  const routeId = useRouterState({ select: state => state.matches.at(-1)?.routeId ?? '' })
  const token = useAuthStore(state => state.accessToken)
  const viewing = useOrgContextStore(state => state.mode !== 'self')
  const persona = useSupplyPersonaStore(state => state.supplyPersona)
  const host = useProblemReportState()
  const shell = isStartCompanyBundle() ? null : reportShell(pathname)
  const me = useQuery({ queryKey: ['me', token], queryFn: fetchMe, enabled: USE_REAL_API && Boolean(token) && Boolean(shell) && !viewing, staleTime: 5 * 60_000 })

  useLayoutEffect(() => {
    let generation = reportHostState().generation
    const stopCache = subscribeReportHost(() => {
      const current = reportHostState()
      if (current.generation === generation) return
      generation = current.generation
      const predicate = (query: { queryKey: readonly unknown[] }) => query.queryKey[0] === 'problem-reports'
        || (typeof query.queryKey[0] === 'string' && /^\/api\/v1\/(?:admin\/)?problem-reports(?:\/|$)/.test(query.queryKey[0]))
      void queryClient.cancelQueries({ predicate })
      queryClient.removeQueries({ predicate })
      const mutations = queryClient.getMutationCache()
      for (const mutation of mutations.getAll()) {
        const key = mutation.options.mutationKey?.[0]
        if (key === 'problem-reports' || (typeof key === 'string' && reportMutationKeys.has(key))) mutations.remove(mutation)
      }
    })
    const stopAuth = useAuthStore.subscribe((next, old) => {
      if (!USE_REAL_API || next.accessToken === old.accessToken) return
      if (!next.accessToken) clearReportIdentity()
      else suspendReportIdentity()
    })
    const stopView = useOrgContextStore.subscribe((next, old) => {
      if (next.mode !== old.mode || next.viewing !== old.viewing) clearReportIdentity()
    })
    const stopPersona = useSupplyPersonaStore.subscribe((next, old) => {
      if (!USE_REAL_API && next.supplyPersona !== old.supplyPersona && reportShell(router.state.location.pathname) === 'supply') clearReportIdentity()
    })
    const stopRoute = router.subscribe('onBeforeNavigate', event => {
      if (reportShell(event.toLocation.pathname) !== reportShell(event.fromLocation?.pathname ?? '')) clearReportIdentity()
    })
    return () => {
      stopAuth(); stopView(); stopPersona(); stopRoute()
      clearReportIdentity()
      stopCache()
    }
  }, [router, queryClient])

  useLayoutEffect(() => {
    if (!shell || viewing || (USE_REAL_API && !token)) { clearReportIdentity(); return }
    let identity: ReportIdentity | null = null
    if (USE_REAL_API) {
      if (me.isPending) { suspendReportIdentity(); return }
      const account = me.data
      const membership = account?.active_membership
      if (account?.user.is_active && membership && shellForOrgType(membership.org_type) === `/${shell}`) {
        identity = { userId: account.user.id, membershipId: membership.id, orgId: membership.org_id, shell }
      }
    } else {
      const key = shell === 'admin' ? 'reviewer' : shell === 'seller' ? 'sarah' : REPORT_SUPPLY_PERSONAS[persona]
      const actor = key ? PROBLEM_REPORT_ACTORS[key] : null
      if (actor) identity = { userId: actor.userId, membershipId: actor.membershipId, orgId: actor.orgId, shell, ...(shell === 'supply' ? { simulatedSupplyPersona: persona } : {}) }
    }
    if (identity) verifyReportIdentity(identity)
    else clearReportIdentity('unavailable')
  }, [shell, token, viewing, persona, me.data, me.isPending])

  const policy = useGetProblemReportCapabilities({ query: {
    queryKey: reportQueryKey('capabilities', token),
    queryFn: ({ signal }) => {
      if (!host.identity) throw reportContextChanged()
      assertReportRequest({ identity: host.identity, generation: host.generation })
      return getProblemReportCapabilities({ signal, headers: { 'X-Problem-Report-Context': host.identity.membershipId } })
    },
    enabled: Boolean(host.identity), retry: false, staleTime: 0, gcTime: 0,
  } })
  useLayoutEffect(() => {
    if (!host.identity || reportHostState().identity !== host.identity) return
    if (policy.isError) setReportPolicy(null)
    else if (policy.data?.status === 200) setReportPolicy(policy.data.data)
  }, [host.identity, policy.data, policy.isError])

  const collecting = Boolean(host.identity && host.policy?.enabled && host.policy.can_self)
  useEffect(() => {
    if (!collecting) return
    const epoch = problemReportEpoch()
    const error = (event: ErrorEvent) => {
      try { recordProblemReportError({ name: event.error?.name, message: event.error?.message ?? event.message }, epoch) } catch { /* Hostile error getters cannot break application handling. */ }
    }
    const rejection = (event: PromiseRejectionEvent) => {
      try { recordProblemReportError({ name: event.reason?.name, message: event.reason?.message }, epoch) } catch { /* Same boundary as synchronous errors. */ }
    }
    window.addEventListener('error', error)
    window.addEventListener('unhandledrejection', rejection)
    return () => { window.removeEventListener('error', error); window.removeEventListener('unhandledrejection', rejection) }
  }, [collecting, host.generation, token])

  const identityKey = host.identity ? reportIdentityKey(host.identity) : null
  useEffect(() => {
    const route = reportRouteName(routeId)
    if (collecting && route !== 'unknown') recordProblemReportAction(`navigation.${route}`, problemReportEpoch())
  }, [collecting, identityKey, routeId])
}
